import { gql } from '@apollo/client';
import { LoadingIcon } from '@assets/icons';
import { SearchAndFilter } from '@components/filter';
import { useGraphqlPagination } from '@components/infiniteScroll';
import { useModal, type ModalProps } from '@components/modal';
import { Typography } from '@components/typography';
import {
  ServiceTypeEnum,
  useAddWorkspacePersistentServicesMutation,
  useAddWorkspaceServicesMutation,
  useGetAvailableServicesForTransferQuery,
  useGetAvailableServicesForTransferSearchDetailQuery,
  useGetServiceOnlyWorkspaceServicesQuery,
  useRemoveWorkspacePersistentServicesMutation,
  useRemoveWorkspaceServicesMutation,
  type GetAvailableServicesForTransferQuery,
  type Service,
  type ServicesFilter,
} from '@generated/graphql';
import { useSearchDetails } from '@hooks/SearchDetails';
import { Stack, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import posthog from 'posthog-js';
import { useCallback, useMemo, useState } from 'react';
import { ResourceListItem, ResourceTransferListButtons } from '../ResourceTransferList';
import { ServiceDetailsModal } from './ServiceDetailsModal';

gql`
  query GetAvailableServicesForTransfer(
    $organizationId: String
    $cursor: String
    $limit: Int
    $filters: ServicesFilter
  ) {
    getServices(
      organizationId: $organizationId
      cursor: $cursor
      limit: $limit
      filters: $filters
    ) {
      name
      serviceId
      serviceType
      persistentServiceId
      description
      createdAt
      updatedAt
    }
  }
`;

gql`
  query GetAvailableServicesForTransferSearchDetail($organizationId: String) {
    getServicesSearchDetail(organizationId: $organizationId) {
      serviceTypes {
        display
        value
      }
    }
  }
`;

type ManageServicesModalProps = {
  workspaceId: string;
  organizationId: string;
  onChange?: VoidFunction;
};

const ManageServicesModal = ({
  workspaceId,
  organizationId,
  onChange,
}: ManageServicesModalProps) => {
  const { palette, breakpoints } = useTheme();
  const isVertical = useMediaQuery(breakpoints.down('md'));

  const [selectedCurrent, setSelectedCurrent] = useState<Set<string>>(new Set());
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [transferring, setTransferring] = useState(false);

  const [addWorkspaceServices] = useAddWorkspaceServicesMutation();
  const [removeWorkspaceServices] = useRemoveWorkspaceServicesMutation();
  const [addWorkspacePersistentServices] = useAddWorkspacePersistentServicesMutation();
  const [removeWorkspacePersistentServices] = useRemoveWorkspacePersistentServicesMutation();

  const { data: currentServicesData, refetch: refetchCurrentServices } =
    useGetServiceOnlyWorkspaceServicesQuery({
      variables: { workspaceId },
      skip: !workspaceId,
    });
  const currentServices = useMemo(
    () => currentServicesData?.getServices || [],
    [currentServicesData?.getServices],
  );

  const { filter, queryFilter, setFilter } = useSearchDetails<ServicesFilter>({
    texts: { type: 'string', value: [], display: [] },
    serviceTypes: { type: 'exact', value: [], display: [] },
  });

  const { data: searchDetailData } = useGetAvailableServicesForTransferSearchDetailQuery({
    variables: { organizationId },
  });
  const searchDetail = searchDetailData?.getServicesSearchDetail;

  const {
    data: allAvailableServices,
    loading: availableLoading,
    fetchMore: fetchMoreAvailable,
  } = useGraphqlPagination<GetAvailableServicesForTransferQuery, Service>(
    useGetAvailableServicesForTransferQuery({
      variables: {
        organizationId,
        limit: 30,
        filters: {
          ...queryFilter,
          serviceTypes: (
            (queryFilter.serviceTypes as unknown as { eq: ServiceTypeEnum }[]) || []
          ).map((s) => s.eq),
        },
      },
    }),
    'getServices',
    'serviceId',
  );

  const currentServiceIds = useMemo(
    () => new Set(currentServices.map((s) => s.serviceId)),
    [currentServices],
  );

  const filteredAvailable = useMemo(
    () =>
      allAvailableServices
        .filter((s) => !currentServiceIds.has(s.serviceId))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [allAvailableServices, currentServiceIds],
  );

  const toggleCurrent = useCallback((id: string) => {
    setSelectedCurrent((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAvailable = useCallback((id: string) => {
    setSelectedAvailable((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddServices = async () => {
    if (selectedAvailable.size === 0) return;
    setTransferring(true);
    try {
      const selectedServices = filteredAvailable.filter((s) => selectedAvailable.has(s.serviceId));
      const regular = selectedServices.filter(
        (s) => s.serviceType !== ServiceTypeEnum.PERSISTENT_SERVICE,
      );
      const persistent = selectedServices.filter(
        (s) => s.serviceType === ServiceTypeEnum.PERSISTENT_SERVICE,
      );

      const promises: Promise<unknown>[] = [];
      if (regular.length > 0) {
        promises.push(
          addWorkspaceServices({
            variables: { workspaceId, serviceIds: regular.map((s) => s.serviceId) },
          }),
        );
      }
      if (persistent.length > 0) {
        promises.push(
          addWorkspacePersistentServices({
            variables: {
              workspaceId,
              persistentServiceIds: persistent
                .map((s) => s.persistentServiceId)
                .filter((id): id is string => !!id),
            },
          }),
        );
      }
      await Promise.all(promises);
      posthog.capture('services:user_add-workspace_services_transfer', { workspaceId });
      setSelectedAvailable(new Set());
      void refetchCurrentServices();
      onChange?.();
    } finally {
      setTransferring(false);
    }
  };

  const handleRemoveServices = async () => {
    if (selectedCurrent.size === 0) return;
    setTransferring(true);
    try {
      const selectedServices = currentServices.filter((s) => selectedCurrent.has(s.serviceId));
      const regular = selectedServices.filter(
        (s) => s.serviceType !== ServiceTypeEnum.PERSISTENT_SERVICE,
      );
      const persistent = selectedServices.filter(
        (s) => s.serviceType === ServiceTypeEnum.PERSISTENT_SERVICE,
      );

      const promises: Promise<unknown>[] = [];
      if (regular.length > 0) {
        promises.push(
          removeWorkspaceServices({
            variables: { workspaceId, serviceIds: regular.map((s) => s.serviceId) },
          }),
        );
      }
      if (persistent.length > 0) {
        promises.push(
          removeWorkspacePersistentServices({
            variables: {
              workspaceId,
              persistentServiceIds: persistent
                .map((s) => s.persistentServiceId)
                .filter((id): id is string => !!id),
            },
          }),
        );
      }
      await Promise.all(promises);
      posthog.capture('services:user_remove-workspace_services_transfer', { workspaceId });
      setSelectedCurrent(new Set());
      void refetchCurrentServices();
      onChange?.();
    } finally {
      setTransferring(false);
    }
  };

  const handleAvailableScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 100) {
        void fetchMoreAvailable();
      }
    },
    [fetchMoreAvailable],
  );

  const { showModal: showServiceDetailsModal } = useModal({
    component: ServiceDetailsModal,
    componentProps: { organizationId },
    modalProps: { sx: { pt: 7.25 } },
  });

  const handleViewServiceDetails = (service: Partial<Service>) => {
    showServiceDetailsModal({ componentProps: { service } });
  };

  return (
    <Stack
      direction={isVertical ? 'column' : 'row'}
      gap={2}
      sx={{ height: '100%', minHeight: '600px', maxHeight: isVertical ? '70vh' : '600px' }}
    >
      {/* Current Workspace Services */}
      <Stack flex={1} sx={{ overflow: 'hidden', minWidth: '400px' }} gap={2}>
        <Typography variant="body2" color="foreground.paper.light">
          Included services ({currentServices.length})
        </Typography>
        <Stack
          sx={{
            flex: 1,
            overflow: 'auto',
            py: 1,
            backgroundColor: palette.background.paperCard,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {currentServices.length === 0 ? (
            <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
              <Typography variant="body2" color="foreground.paperCard.light">
                No services attached
              </Typography>
            </Stack>
          ) : (
            [...currentServices]
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((service) => (
                <ResourceListItem<Service>
                  key={service.serviceId}
                  resource={service}
                  idKey="serviceId"
                  tag={
                    service.serviceType === ServiceTypeEnum.PERSISTENT_SERVICE
                      ? 'Persistent'
                      : service.serviceType === ServiceTypeEnum.MARKETPLACE
                        ? 'Marketplace'
                        : undefined
                  }
                  selected={selectedCurrent.has(service.serviceId)}
                  onToggle={toggleCurrent}
                  onViewDetails={handleViewServiceDetails}
                />
              ))
          )}
        </Stack>
      </Stack>

      {/* Transfer Buttons */}
      <ResourceTransferListButtons
        onAdd={handleAddServices}
        onRemove={handleRemoveServices}
        isVertical={isVertical}
        addDisabled={selectedAvailable.size === 0 || transferring}
        removeDisabled={selectedCurrent.size === 0 || transferring}
      />
      {/* Available Organization Services */}
      <Stack flex={1} sx={{ overflow: 'hidden', minWidth: '400px' }}>
        <Stack gap={2}>
          <Typography variant="body2" color="foreground.paper.light">
            Available services
          </Typography>
          <SearchAndFilter
            context="manage-services-transfer"
            filter={filter}
            setFilter={setFilter}
            searchDetail={searchDetail}
            variant="secondary"
          />
        </Stack>
        <Stack
          sx={{
            flex: 1,
            overflow: 'auto',
            py: 1,
            backgroundColor: palette.background.paperCard,
            border: '1px solid',
            borderColor: 'divider',
          }}
          onScroll={handleAvailableScroll}
        >
          {availableLoading ? (
            <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
              <LoadingIcon size={16} />
            </Stack>
          ) : filteredAvailable.length === 0 ? (
            <Stack flex={1} alignItems="center" justifyContent="center" p={4}>
              <Typography variant="body2" color="foreground.paperCard.light">
                No available services found
              </Typography>
            </Stack>
          ) : (
            filteredAvailable.map((service) => (
              <ResourceListItem<Service>
                key={service.serviceId}
                resource={service}
                idKey="serviceId"
                tag={
                  service.serviceType === ServiceTypeEnum.PERSISTENT_SERVICE
                    ? 'Persistent'
                    : service.serviceType === ServiceTypeEnum.MARKETPLACE
                      ? 'Marketplace'
                      : undefined
                }
                selected={selectedAvailable.has(service.serviceId)}
                onToggle={toggleAvailable}
                onViewDetails={handleViewServiceDetails}
              />
            ))
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export const useManageServicesModal = ({
  componentProps,
  modalProps,
}: {
  componentProps: Omit<ManageServicesModalProps, 'onClose'> & { onClose?: () => void };
  modalProps?: Partial<ModalProps>;
}) => {
  const { showModal } = useModal({
    component: ManageServicesModal,
    componentProps,
    modalProps: {
      sx: {
        width: '90vw',
        maxWidth: '1000px',
        display: 'flex',
        flexDirection: 'column',
        '& form': { flex: 1, height: '100%', '& .MuiFormGroup-root': { flex: 1, height: '100%' } },
      },
      ...modalProps,
    },
  });

  const showManageServicesModal = (dynamicComponentProps?: Partial<ManageServicesModalProps>) => {
    showModal({ componentProps: dynamicComponentProps });
  };

  return { showManageServicesModal };
};
